import { Authenticate, layoutRoute } from '@/router/root-routes.tsx';
import { createRoute, useLocation } from '@tanstack/react-router';
import { FunnelIcon } from 'lucide-react';
import { Button } from '@/components/ui/button.tsx';
import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils.ts';
import { FacetsSidebar } from '@/app/products/list/facets-sidebar.tsx';
import { useProductsQuery } from '@/app/products/api/queries.ts';
import { usePageProps } from '@/hooks/use-page-props.ts';
import { Badge } from '@/components/ui/badge.tsx';
import { ProductSearchInput } from '@/app/products/list/product-search-input.tsx';
import { ProductItem } from '@/app/products/list/product-item.tsx';
import { Spinner } from '@/components/app/spinner.tsx';
import { useSearchParams } from '@/hooks/use-search-params.ts';
import { getProductsShowingText, Pagination } from '@/app/products/list/pagination.tsx';
import { RemoveFilters } from '@/app/products/list/remove-filters.tsx';
import { useAuthStore } from '@/stores/auth.store.ts';
import { AddToProjectDrawer } from '@/app/products/add-to-project/add-to-project-drawer.tsx';
import { useUiStore } from '@/stores/ui.store.ts';

export const productsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: 'products',
  component: ProductsPage,
  beforeLoad: Authenticate(),
});

export const rugsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: 'rugs',
  component: ProductsPage,
  beforeLoad: Authenticate(),
});

export function ProductsPage() {
  const location = useLocation();
  const isRugsPage = location.pathname === '/rugs';
  usePageProps({
    title: isRugsPage ? 'Rugs' : 'Products',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedColor, setSelectedColor] = useState(undefined as number | undefined);
  const useSearch = useSearchParams();
  const { data, isLoading, isPlaceholderData } = useProductsQuery({
    ...useSearch.params,
    page_size: 24,
    rugs: isRugsPage ? 1 : undefined,
  });
  const user = useAuthStore((state) => state.user);
  const addToProjectItem = useUiStore((state) => state.addToProjectItem);
  const adminUpdated = useUiStore((state) => state.uiUpdate);

  useEffect(() => {
    if (data && !isPlaceholderData && data.filters) {
      let c_p: undefined | number = undefined;
      data.filters.forEach((filter) => {
        if (filter.key === 'c_p') {
          c_p = Number(filter.value);
        }
      });
      setSelectedColor(c_p);
    }
  }, [data, isPlaceholderData]);

  if (!user) {
    return null;
  }

  return (
    <div className="px-4 py-2 xl:px-8 max-w-[1900px] mx-auto">
      <div className="flex justify-between items-center mb-3">
        <h1 className="uppercase text-3xl font-medium lg:hidden">Products</h1>
        <div>
          <Button
            variant="ghost"
            className={cn('mx-1 lg:ml-8 xl:ml-24 lg:hidden', showFilters && 'bg-gray-200')}
            onClick={() => setShowFilters(!showFilters)}
          >
            <div className="relative inline-block">
              <FunnelIcon className="h-6 w-6" />
              <Badge
                variant="destructive"
                className="absolute -top-2 -right-2 h-4 w-4 flex items-center justify-center p-0 rounded-full text-xs"
              >
                {data?.filters?.length}
              </Badge>
            </div>
          </Button>
        </div>
      </div>
      <div className="flex flex-col lg:flex-row">
        <div className={cn('w-full lg:max-w-60', showFilters ? 'block' : 'hidden lg:block lg:mr-8')}>
          <h1 className="uppercase text-3xl font-medium hidden lg:block mb-3">Products</h1>
          <ProductSearchInput useSearch={useSearch} />
          <FacetsSidebar data={data} useSearch={useSearch} isRugsPage={isRugsPage} />
        </div>
        <div className="grow relative">
          <div
            className={cn(
              'absolute top-20 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-10',
              isLoading || isPlaceholderData ? 'block' : 'hidden',
            )}
          >
            <div className="flex items-center gap-6">
              <Spinner className="size-24 opacity-50" />
            </div>
          </div>
          <div className="flex flex-col lg:flex-row items-center lg:justify-between mb-3 gap-3">
            {data && (
              <>
                <RemoveFilters data={data} useSearch={useSearch} />
                <div>{getProductsShowingText(data)}</div>
              </>
            )}
          </div>
          <div key={adminUpdated} className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-8 products-grid mb-8">
            {data?.items.map((item, i) => (
              <ProductItem key={item.id + '-' + i} item={item} selectedColor={selectedColor} user={user} />
            ))}
          </div>
          {data && <Pagination data={data} useSearch={useSearch} />}
        </div>
      </div>
      {addToProjectItem && <AddToProjectDrawer />}
    </div>
  );
}
